import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
  MasterFormatViewToggle,
  LocationInput,
} from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { useToast } from '../../../shared/context/ToastContext';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { formatCurrency, formatUnitCost, formatUnitCostWithUnit, formatSqFt, formatDate, formatNumber, formatPercentTrim } from '../../../shared/utils/formatters';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { NearestNeighborsPanel } from './NearestNeighborsPanel';
import { LineItemExplanationPanel } from './LineItemExplanationPanel';
import { useMasterFormatView, lineItemDescriptionMatchesSources } from '../hooks/useMasterFormatView';
import { getLegacyDivisionSubtitle } from '../../../shared/utils/masterformatMapping';
import { useTenantPreferences } from '../../../shared/hooks/useTenantPreferences';
import { EstimateDocumentsPanel } from './EstimateDocumentsPanel';

function CostSourceBadge({ item }) {
  // Uploaded from estimate file - amber badge
  if (item.costSource === 'uploaded') {
    return <Badge variant="yellow" size="sm">Uploaded</Badge>;
  }

  // Manual or Template - gray badge
  if (item.costSource === 'manual' || item.costSource === 'template') {
    return <Badge variant="gray" size="sm">Manual</Badge>;
  }

  // User Adjusted - yellow badge with variance %
  if (item.costSource === 'user_adjusted' && item.aiEstimatedCost && item.userAdjustedCost) {
    const variance = ((item.userAdjustedCost - item.aiEstimatedCost) / item.aiEstimatedCost) * 100;
    const isPositive = variance > 0;
    return (
      <div className="flex flex-col items-end">
        <Badge variant="yellow" size="sm">Adjusted</Badge>
        <span className={`text-xs font-medium ${isPositive ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
          {isPositive ? '+' : ''}{variance.toFixed(0)}%
        </span>
      </div>
    );
  }

  // AI Generated - blue badge
  if (item.costSource === 'ai') {
    return <Badge variant="blue" size="sm">AI</Badge>;
  }

  // Fallback (no costSource set)
  return <span className="text-gray-400 dark:text-gray-500">—</span>;
}

function CategorySourceSummary({ lineItems }) {
  if (!lineItems || lineItems.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  const counts = lineItems.reduce((acc, item) => {
    const source = item.costSource || 'manual';
    if (source === 'ai') acc.ai++;
    else if (source === 'user_adjusted') acc.adjusted++;
    else if (source === 'uploaded') acc.uploaded++;
    else acc.manual++;
    return acc;
  }, { ai: 0, adjusted: 0, manual: 0, uploaded: 0 });

  const parts = [];
  if (counts.ai > 0) parts.push(<span key="ai" className="text-blue-600 dark:text-blue-400">{counts.ai} AI</span>);
  if (counts.adjusted > 0) parts.push(<span key="adj" className="text-yellow-600 dark:text-yellow-400">{counts.adjusted} Adj</span>);
  if (counts.uploaded > 0) parts.push(<span key="upl" className="text-amber-600 dark:text-amber-400">{counts.uploaded} Upl</span>);
  if (counts.manual > 0) parts.push(<span key="man" className="text-gray-500 dark:text-gray-400">{counts.manual} Man</span>);

  if (parts.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  return (
    <div className="text-xs flex flex-wrap justify-end gap-1">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-300">/</span>}
          {part}
        </React.Fragment>
      ))}
    </div>
  );
}

function MobileFieldsPanel({ item, categoryCode }) {
  const rows = [
    { label: 'Qty', value: item.quantity ? formatNumber(item.quantity) : '—' },
    { label: 'Unit', value: item.unit || '—' },
    {
      label: 'Unit Cost',
      value: categoryCode === '00'
        ? formatPercentTrim(item.totalCost)
        : (item.unitCost ? formatUnitCostWithUnit(item.unitCost, item.unit) : '—'),
    },
    { label: 'Source', value: <CostSourceBadge item={item} /> },
  ];
  return (
    <dl className="md:hidden grid grid-cols-2 gap-x-4 gap-y-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-800 text-sm">
      {rows.map(({ label, value }) => (
        <React.Fragment key={label}>
          <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
          <dd className="text-right text-gray-800 dark:text-gray-200">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function LineItemRow({ item, estimateId, categoryId, categoryCode, baseTotalCost = 0, isExpanded, onToggle }) {
  const [neighbors, setNeighbors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if item has AI similar items stored
  const hasStoredSimilarItems = item.aiSimilarItems && item.aiSimilarItems.length > 0;
  const hasAIEstimate = item.aiExplanation || item.costSource === 'ai' || item.costSource === 'user_adjusted';

  // The expand affordance should appear only when the dropdown has real
  // content to render. The explanation panel returns null for:
  //   - virtual source rows (Custom view's per-source split)
  //   - uploaded rows whose description already lists the sources
  //   - uploaded rows with no source_line_items at all (placeholder copy
  //     duplicated the "Uploaded" badge — not worth a dropdown)
  // Mirror that logic here so an uploaded row with no sources doesn't
  // render a chevron that opens an empty drawer.
  const uploadedHasSourceDetail =
    item.costSource === 'uploaded'
    && Array.isArray(item.sourceLineItems)
    && item.sourceLineItems.length > 0
    && !lineItemDescriptionMatchesSources(item);

  const isExpandable = item._isVirtualSourceRow
    ? false
    : hasAIEstimate
      || hasStoredSimilarItems
      || uploadedHasSourceDetail;

  useEffect(() => {
    // Only load dynamic neighbors if we don't have stored similar items
    if (isExpanded && !hasStoredSimilarItems && !neighbors && !loading && !error) {
      loadNeighbors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  async function loadNeighbors() {
    setLoading(true);
    setError(null);
    try {
      const data = await estimatesApi.getNearestNeighborsForLineItem(
        estimateId,
        categoryId,
        item.lineItemId
      );
      setNeighbors(data.neighbors || []);
    } catch (err) {
      console.error('Failed to load neighbors:', err);
      setError(err.message || 'Failed to load similar items');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <tr
        id={`line-item-${item.lineItemId}`}
        onClick={isExpandable ? onToggle : undefined}
        className={
          'border-b border-gray-100 dark:border-gray-800 transition-colors '
          + (isExpandable
              ? 'hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer'
              : '')
        }
      >
        <td className="py-2 px-4 pl-10 align-top">
          <div className="flex items-start gap-2">
            {isExpandable ? (
              <svg
                className={`w-3 h-3 mt-1.5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              // Spacer keeps the description column aligned with expandable rows.
              <span className="w-3 h-3 inline-block flex-shrink-0" aria-hidden="true" />
            )}
            <span className="text-gray-700 dark:text-gray-200 break-words min-w-0">{item.description}</span>
          </div>
          {/* Mobile-only inline summary so non-expandable rows still show qty/unit/source. */}
          <div className="md:hidden ml-5 mt-1 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
            {(item.quantity || item.unit) && (
              <span>{item.quantity ? formatNumber(item.quantity) : '—'} {item.unit || ''}</span>
            )}
            {categoryCode !== '00' && item.unitCost && (
              <>
                <span aria-hidden="true">·</span>
                <span>{formatUnitCostWithUnit(item.unitCost, item.unit)}</span>
              </>
            )}
            <CostSourceBadge item={item} />
          </div>
          {item.notes && !isExpanded && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-5">{item.notes}</p>
          )}
        </td>
        <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm">
          {item.quantity ? formatNumber(item.quantity) : '—'}
        </td>
        <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm">
          {item.unit || '—'}
        </td>
        <td className="hidden md:table-cell py-2 px-4 text-right text-gray-600 dark:text-gray-300 text-sm">
          {categoryCode === '00' ? formatPercentTrim(item.totalCost) : (item.unitCost ? formatUnitCostWithUnit(item.unitCost, item.unit) : '—')}
        </td>
        <td className="py-2 px-4 text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">
          {categoryCode === '00' ? formatCurrency(baseTotalCost * (item.totalCost / 100)) : formatCurrency(item.totalCost)}
        </td>
        <td className="hidden md:table-cell py-2 px-4 text-right">
          <CostSourceBadge item={item} />
        </td>
        <td className="hidden md:table-cell py-2 px-4 text-right text-gray-500 dark:text-gray-400 text-sm">—</td>
      </tr>
      {isExpanded && isExpandable && (
        <tr>
          <td colSpan="7" className="bg-gray-50 dark:bg-gray-900 p-4 border-b border-gray-200 dark:border-gray-800">
            {/* On phones the secondary table columns are hidden — surface
                them here so the expanded row still shows the full picture. */}
            <MobileFieldsPanel item={item} categoryCode={categoryCode} />
            {/* Use LineItemExplanationPanel for items with AI estimates or stored similar items */}
            {(hasAIEstimate || hasStoredSimilarItems || item.costSource === 'manual' || item.costSource === 'template' || item.costSource === 'uploaded') ? (
              <LineItemExplanationPanel item={item} categoryCode={categoryCode} />
            ) : loading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="md" />
                <span className="ml-2 text-gray-600 dark:text-gray-300">Loading similar items...</span>
              </div>
            ) : error ? (
              <div className="text-center py-6 text-red-600 dark:text-red-400">
                <p>{error}</p>
              </div>
            ) : neighbors ? (
              <NearestNeighborsPanel item={item} neighbors={neighbors} />
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

function CategoryRow({ category, estimateId, baseTotalCost = 0, isExpanded, onToggle, expandedLineItems, onToggleLineItem, isLegacyView = false }) {
  // For Fee category (code '00'), use subtotal as the percentage; for others use percentOfTotal
  const percentOfTotal = category.categoryCode === '00'
    ? formatPercentTrim(category.subtotal)
    : (category.percentOfTotal ? formatPercentTrim(category.percentOfTotal) : '');

  // Get subtitle for legacy divisions that consolidate multiple modern divisions
  const legacySubtitle = isLegacyView && category.isLegacyGroup
    ? getLegacyDivisionSubtitle(category.legacyDivisionNumber)
    : null;

  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
      >
        <td className="py-3 px-4 align-top">
          <div className="flex items-start gap-2">
            <svg
              className={`w-4 h-4 mt-0.5 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <div className="min-w-0">
              <span className="font-medium text-gray-900 dark:text-gray-100 break-words">
                {category.categoryCode ? `${category.categoryCode.substring(0, 2)}. ` : ''}
                {category.categoryName}
              </span>
              {legacySubtitle && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{legacySubtitle}</p>
              )}
              {/* Mobile-only inline summary: items count + percent (Total is in its own visible cell). */}
              <p className="md:hidden text-xs text-gray-500 dark:text-gray-400 mt-1">
                {category.lineItems?.length || 0} items{percentOfTotal ? ` · ${percentOfTotal}` : ''}
              </p>
            </div>
          </div>
        </td>
        <td className="hidden md:table-cell py-3 px-4 text-right text-gray-500 dark:text-gray-400">
          {category.lineItems?.length || 0} items
        </td>
        <td className="hidden md:table-cell py-3 px-4 text-right text-gray-500 dark:text-gray-400">—</td>
        <td className="hidden md:table-cell py-3 px-4 text-right text-gray-500 dark:text-gray-400">—</td>
        <td className="py-3 px-4 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
          {category.categoryCode === '00'
            ? formatCurrency(baseTotalCost * (category.subtotal / 100))
            : formatCurrency(category.subtotal)}
        </td>
        <td className="hidden md:table-cell py-3 px-4 text-right">
          <CategorySourceSummary lineItems={category.lineItems} />
        </td>
        <td className="hidden md:table-cell py-3 px-4 text-right text-gray-500 dark:text-gray-400">
          {percentOfTotal}
        </td>
      </tr>
      {isExpanded && category.lineItems?.map((item) => (
        <LineItemRow
          key={item.lineItemId}
          item={item}
          estimateId={estimateId}
          categoryId={category.categoryId}
          categoryCode={category.categoryCode}
          baseTotalCost={baseTotalCost}
          isExpanded={expandedLineItems.has(item.lineItemId)}
          onToggle={(e) => {
            e.stopPropagation();
            onToggleLineItem(item.lineItemId);
          }}
        />
      ))}
    </>
  );
}

function CostBreakdownTable({ categories, totalCost, baseTotalCost = 0, estimateId, expandedCategories, setExpandedCategories, expandedLineItems, setExpandedLineItems, isLegacyView = false, flatten = false }) {
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleLineItem = (lineItemId) => {
    setExpandedLineItems((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) {
        next.delete(lineItemId);
      } else {
        next.add(lineItemId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(categories.map((c) => c.categoryId)));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedLineItems(new Set());
  };

  // Add percent of total to each category and sort by categoryCode
  const categoriesWithPercent = categories
    .map((cat) => ({
      ...cat,
      percentOfTotal: totalCost > 0 ? (cat.subtotal / totalCost) * 100 : 0,
    }))
    .sort((a, b) => (a.categoryCode || '').localeCompare(b.categoryCode || ''));

  // In flat mode (sub-trade tenants), pull every line item out of every
  // category into a single ordered list so we can render them without the
  // CategoryRow wrappers. Categories are still iterated in code-order so
  // the relative ordering is stable across renders.
  const flatLineItems = flatten
    ? categoriesWithPercent.flatMap((cat) =>
        (cat.lineItems || []).map((item) => ({ item, category: cat }))
      )
    : [];

  return (
    <div>
      {!flatten && (
        <div className="flex justify-end gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800">
              <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
                {flatten ? 'Item' : 'Category / Item'}
              </th>
              <th className="hidden md:table-cell text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-24">
                Qty
              </th>
              <th className="hidden md:table-cell text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-20">
                Unit
              </th>
              <th className="hidden md:table-cell text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-28">
                Unit Cost
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-32">
                Total
              </th>
              <th className="hidden md:table-cell text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-24">
                Source
              </th>
              <th className="hidden md:table-cell text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-20">
                % Total
              </th>
            </tr>
          </thead>
          <tbody>
            {flatten
              ? flatLineItems.map(({ item, category }) => (
                  <LineItemRow
                    key={item.lineItemId}
                    item={item}
                    estimateId={estimateId}
                    categoryId={category.categoryId}
                    categoryCode={category.categoryCode}
                    baseTotalCost={baseTotalCost}
                    isExpanded={expandedLineItems.has(item.lineItemId)}
                    onToggle={() => toggleLineItem(item.lineItemId)}
                  />
                ))
              : categoriesWithPercent.map((category) => (
                  <CategoryRow
                    key={category.categoryId}
                    category={category}
                    estimateId={estimateId}
                    baseTotalCost={baseTotalCost}
                    isExpanded={expandedCategories.has(category.categoryId)}
                    onToggle={() => toggleCategory(category.categoryId)}
                    expandedLineItems={expandedLineItems}
                    onToggleLineItem={toggleLineItem}
                    isLegacyView={isLegacyView}
                  />
                ))}
            {/* Total row */}
            <tr className="border-t-2 border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
              <td className="py-3 px-4 font-bold text-gray-900 dark:text-gray-100" colSpan={4}>
                Project Total
              </td>
              <td className="py-3 px-4 text-right font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {formatCurrency(totalCost)}
              </td>
              <td className="hidden md:table-cell py-3 px-4 text-right text-gray-500 dark:text-gray-400">—</td>
              <td className="hidden md:table-cell py-3 px-4 text-right text-gray-700 dark:text-gray-200">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectInfo({ estimate, estimateId, onUpdate, isDraft, isSubcontractor = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [formData, setFormData] = useState({});
  const [locationValidated, setLocationValidated] = useState(true);

  const startEditing = () => {
    setFormData({
      jobType: estimate.jobType || '',
      squareFootage: estimate.squareFootage ?? '',
      location: estimate.location || '',
      constructionType: estimate.constructionType || '',
      stories: estimate.stories ?? '',
      numberOfBuildings: estimate.numberOfBuildings ?? '',
      prevailingWage: estimate.prevailingWage || false,
      startDate: estimate.startDate || '',
      projectTimelineMonths: estimate.projectTimelineMonths ?? '',
      estimateDate: estimate.estimateDate || '',
    });
    setLocationValidated(true);
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updates = {
        jobType: formData.jobType || null,
        squareFootage: formData.squareFootage !== '' ? Number(formData.squareFootage) : null,
        location: formData.location || null,
        constructionType: formData.constructionType || null,
        stories: formData.stories !== '' ? Number(formData.stories) : null,
        numberOfBuildings: formData.numberOfBuildings !== '' ? Number(formData.numberOfBuildings) : null,
        prevailingWage: formData.prevailingWage,
        startDate: formData.startDate || null,
        projectTimelineMonths: formData.projectTimelineMonths !== '' ? Number(formData.projectTimelineMonths) : null,
        estimateDate: formData.estimateDate || null,
      };
      await estimatesApi.updateEstimate(estimateId, updates);
      onUpdate(updates);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const inputClass = "w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  if (!isEditing) {
    return (
      <div>
        {isDraft && (
          <div className="flex justify-end mb-4">
            <button
              onClick={startEditing}
              className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Details
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {!isSubcontractor && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Job Type</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.jobType}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Square Footage</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{formatSqFt(estimate.squareFootage)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.location}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Construction Type</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.constructionType || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Stories</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.stories || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Number of Buildings</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.numberOfBuildings || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Prevailing Wage</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.prevailingWage ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Start Date</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{estimate.startDate ? formatDate(estimate.startDate) : '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Timeline</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {estimate.projectTimelineMonths ? `${estimate.projectTimelineMonths} months` : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Estimate Date</p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(estimate.estimateDate)}</p>
          </div>
          {estimate.completedDate && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Completed Date</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(estimate.completedDate)}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {!isSubcontractor && (
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Job Type</label>
            <input
              type="text"
              value={formData.jobType}
              onChange={e => handleChange('jobType', e.target.value)}
              className={inputClass}
              placeholder="e.g. Retail, Warehouse"
            />
          </div>
        )}
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Square Footage</label>
          <input
            type="number"
            value={formData.squareFootage}
            onChange={e => handleChange('squareFootage', e.target.value)}
            className={inputClass}
            placeholder="e.g. 45000"
            min="0"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Location</label>
          <LocationInput
            value={formData.location}
            onChange={(val) => handleChange('location', val)}
            onValidationChange={setLocationValidated}
            placeholder="e.g. Cincinnati, OH"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Construction Type</label>
          <input
            type="text"
            value={formData.constructionType}
            onChange={e => handleChange('constructionType', e.target.value)}
            className={inputClass}
            placeholder="e.g. Steel Frame"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Stories</label>
          <input
            type="number"
            value={formData.stories}
            onChange={e => handleChange('stories', e.target.value)}
            className={inputClass}
            placeholder="e.g. 1"
            min="1"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Number of Buildings</label>
          <input
            type="number"
            value={formData.numberOfBuildings}
            onChange={e => handleChange('numberOfBuildings', e.target.value)}
            className={inputClass}
            placeholder="e.g. 1"
            min="1"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Prevailing Wage</label>
          <div className="flex items-center gap-2 mt-1.5">
            <button
              type="button"
              onClick={() => handleChange('prevailingWage', !formData.prevailingWage)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                formData.prevailingWage ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
              role="switch"
              aria-checked={formData.prevailingWage}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 shadow ring-0 transition duration-200 ease-in-out ${
                  formData.prevailingWage ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {formData.prevailingWage ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Start Date</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={e => handleChange('startDate', e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Timeline (months)</label>
          <input
            type="number"
            value={formData.projectTimelineMonths}
            onChange={e => handleChange('projectTimelineMonths', e.target.value)}
            className={inputClass}
            placeholder="e.g. 18"
            min="1"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Estimate Date</label>
          <input
            type="date"
            value={formData.estimateDate}
            onChange={e => handleChange('estimateDate', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {saveError && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300 text-sm">
          {saveError}
        </div>
      )}

      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-800">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || (formData.location?.trim() && !locationValidated)}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function CostSummary({ estimate }) {
  // `actualCost != null` so a legitimate zero still computes a variance
  // (rare but valid -- e.g. a fully-discounted job).
  const variance = estimate.actualCost != null && estimate.totalCost
    ? ((estimate.actualCost - estimate.totalCost) / estimate.totalCost) * 100
    : null;

  // Use fee-aware totals from backend, falling back to totalCost for older estimates
  const hasFee = estimate.feePercentage > 0;
  const displayTotal = hasFee ? estimate.grandTotal : estimate.totalCost;

  const sqFt = estimate.squareFootage > 0 ? estimate.squareFootage : null;
  const months = estimate.projectTimelineMonths > 0 ? estimate.projectTimelineMonths : null;

  const rowClass = hasFee
    ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
    : 'grid grid-cols-1 gap-4';

  return (
    <div className="space-y-3">
      {/* Row 1: Main totals */}
      <div className={rowClass}>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400">Estimated Total</p>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{formatCurrency(displayTotal)}</p>
        </div>
        {hasFee && (
          <>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">Base Cost</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(estimate.baseTotalCost)}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
              <p className="text-sm text-purple-600 dark:text-purple-400">Fee ({formatPercentTrim(estimate.feePercentage)})</p>
              <p className="text-xl font-bold text-purple-900 dark:text-purple-200">{formatCurrency(estimate.feeAmount)}</p>
            </div>
          </>
        )}
      </div>

      {/* Row 2: Per sq ft — only shown when square footage is set */}
      {sqFt && (
        <div className={rowClass}>
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
            <p className="text-sm text-blue-600 dark:text-blue-400">/ sq ft</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{formatUnitCost(displayTotal / sqFt)}</p>
          </div>
          {hasFee && (
            <>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">/ sq ft</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatUnitCost(estimate.baseTotalCost / sqFt)}</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                <p className="text-sm text-purple-600 dark:text-purple-400">/ sq ft</p>
                <p className="text-xl font-bold text-purple-900 dark:text-purple-200">{formatUnitCost(estimate.feeAmount / sqFt)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Row 3: Per month — only shown when project timeline is set */}
      {months && (
        <div className={rowClass}>
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
            <p className="text-sm text-blue-600 dark:text-blue-400">/ month</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{formatCurrency(displayTotal / months)}</p>
          </div>
          {hasFee && (
            <>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">/ month</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(estimate.baseTotalCost / months)}</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                <p className="text-sm text-purple-600 dark:text-purple-400">/ month</p>
                <p className="text-xl font-bold text-purple-900 dark:text-purple-200">{formatCurrency(estimate.feeAmount / months)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Actual Cost / Variance -- shown when the estimate was stitched
          to a Jobber invoice. `!= null` preserves zero. */}
      {estimate.actualCost != null && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
            <p className="text-sm text-green-600 dark:text-green-400">Actual Cost</p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-200">{formatCurrency(estimate.actualCost)}</p>
          </div>
          <div className={`rounded-lg p-4 ${variance > 0 ? 'bg-red-50 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
            <p className={`text-sm ${variance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              Variance
            </p>
            <p className={`text-2xl font-bold ${variance > 0 ? 'text-red-900 dark:text-red-200' : 'text-green-900 dark:text-green-200'}`}>
              {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const CHART_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7'
];

function CostBreakdownChart({ categories, totalCost, baseTotalCost = 0, onCategoryClick, onLineItemClick, isLegacyView = false }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // When the estimate has exactly one category (common for sub-trade
  // tenants), a 100% pie chart of "the only category" is useless. Auto-
  // drill into that category so the user lands on the line item breakdown.
  // Re-runs if the categories list itself changes (e.g. the user toggles
  // CSI 50 -> CSI 15 -> Custom view) so the selection always tracks the
  // current single category.
  const onlyCategory = categories && categories.length === 1 ? categories[0] : null;
  const onlyCategoryId = onlyCategory ? onlyCategory.categoryId : null;
  useEffect(() => {
    if (onlyCategory && selectedCategory?.categoryId !== onlyCategoryId) {
      setSelectedCategory(onlyCategory);
    }
  }, [onlyCategoryId, onlyCategory, selectedCategory]);

  if (!categories || categories.length === 0) {
    return null;
  }

  // No useful "back" target when there's only one category -- hide the
  // back arrow and the View All button so the user can't get stuck on a
  // pointless 100% pie.
  const showBackToCategories = selectedCategory && categories.length > 1;

  // Determine what data to show based on selected category
  // NOTE: Convert string values to numbers for Recharts (API returns numeric values as strings)
  // For Fee category (code '00'), subtotal is a percentage, so calculate the actual dollar amount
  const getCategoryValue = (cat) => {
    if (cat.categoryCode === '00') {
      // Fee category: subtotal is percentage, calculate dollar amount
      return baseTotalCost * (Number(cat.subtotal) / 100);
    }
    return Number(cat.subtotal);
  };

  const getLineItemValue = (item, categoryCode) => {
    if (categoryCode === '00') {
      // Fee line item: totalCost is percentage, calculate dollar amount
      return baseTotalCost * (Number(item.totalCost) / 100);
    }
    return Number(item.totalCost);
  };

  const currentData = selectedCategory
    ? // Show line items for selected category
      selectedCategory.lineItems?.map((item) => {
        const value = getLineItemValue(item, selectedCategory.categoryCode);
        const categoryValue = getCategoryValue(selectedCategory);
        return {
          name: item.description,
          value,
          percentOfTotal: categoryValue > 0 ? (value / categoryValue) * 100 : 0,
          lineItemId: item.lineItemId,
          categoryId: selectedCategory.categoryId,
        };
      }) || []
    : // Show all categories
      categories.map((cat) => {
        const value = getCategoryValue(cat);
        return {
          name: cat.categoryName,
          value,
          percentOfTotal: Number(totalCost) > 0 ? (value / Number(totalCost)) * 100 : 0,
          categoryId: cat.categoryId,
          category: cat,
        };
      });

  const currentTotal = selectedCategory ? getCategoryValue(selectedCategory) : Number(totalCost);

  const handlePieClick = (data, index) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 400);

    if (selectedCategory) {
      // Drilled down to line items - clicking scrolls to that line item
      const lineItem = currentData[index];
      if (lineItem && lineItem.lineItemId && onLineItemClick) {
        onLineItemClick(lineItem.categoryId, lineItem.lineItemId);
      }
      return;
    }

    // Drill down into category
    const category = categories[index];
    if (category && category.lineItems && category.lineItems.length > 0) {
      setSelectedCategory(category);
      // Also expand in the table
      if (onCategoryClick) {
        onCategoryClick(category.categoryId);
      }
    }
  };

  const handleBackClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 400);
    setSelectedCategory(null);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-900 p-3 shadow-lg rounded border border-gray-200 dark:border-gray-800">
          <p className="font-medium text-gray-900 dark:text-gray-100">{payload[0].name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {formatCurrency(payload[0].value)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {payload[0].payload.percentOfTotal.toFixed(1)}% of{' '}
            {selectedCategory ? 'category' : 'total'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            {selectedCategory ? (
              <div className="flex items-center gap-2">
                {showBackToCategories && (
                  <button
                    onClick={handleBackClick}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    title="Back to categories"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <span>{selectedCategory.categoryName} - Line Items</span>
              </div>
            ) : (
              'Cost Breakdown by Category'
            )}
          </CardTitle>
          {showBackToCategories && (
            <button
              onClick={handleBackClick}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium"
            >
              View All Categories
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div>
            {[
              { wrapper: 'md:hidden', height: 260, outerRadius: 80 },
              { wrapper: 'hidden md:block', height: 400, outerRadius: 120 },
            ].map(({ wrapper, height, outerRadius }) => (
              <div key={wrapper} className={wrapper}>
                <ResponsiveContainer width="100%" height={height}>
                  <PieChart>
                    <Pie
                      data={currentData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={outerRadius}
                      label={(entry) => `${entry.percentOfTotal.toFixed(1)}%`}
                      labelLine={{ stroke: '#999', strokeWidth: 1 }}
                      onClick={handlePieClick}
                      cursor="pointer"
                      animationBegin={0}
                      animationDuration={400}
                      animationEasing="ease-in-out"
                      isAnimationActive={true}
                    >
                      {currentData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                          className="hover:opacity-80 transition-opacity"
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ))}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
              {selectedCategory
                ? 'Click a slice to scroll to line item details'
                : 'Click a slice to view line items'}
            </p>
          </div>

          {/* Legend */}
          <div>
            <h4
              className="font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-opacity duration-300"
              style={{
                animation: 'fadeIn 0.3s ease-in-out'
              }}
            >
              {selectedCategory ? 'Line Item Summary' : 'Category Summary'}
            </h4>
            <div className="space-y-2">
              {currentData.map((item, idx) => (
                <div
                  key={`${selectedCategory ? 'item' : 'cat'}-${idx}`}
                  className="flex justify-between items-center py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-2 rounded transition-all duration-200"
                  style={{
                    animation: `fadeInSlide 0.4s ease-out ${idx * 0.05}s both`,
                    opacity: 0,
                  }}
                  onClick={() => {
                    if (selectedCategory && item.lineItemId) {
                      // Click on line item - scroll to it
                      if (onLineItemClick) {
                        onLineItemClick(item.categoryId, item.lineItemId);
                      }
                    } else if (!selectedCategory && item.category) {
                      // Click on category - drill down
                      handlePieClick(item, idx);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 rounded flex-shrink-0 transition-all duration-300"
                      style={{
                        backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                        transform: !selectedCategory ? 'scale(1)' : 'scale(1)',
                      }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{item.name}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 ml-3">
                    {formatCurrency(item.value)}
                  </div>
                </div>
              ))}
            </div>
            {selectedCategory && (
              <div
                className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800"
                style={{
                  animation: 'fadeIn 0.5s ease-in-out 0.3s both',
                  opacity: 0,
                }}
              >
                <div className="flex justify-between items-center font-semibold text-gray-900 dark:text-gray-100">
                  <span>Category Total</span>
                  <span>{formatCurrency(currentTotal)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusToggle({ status, onToggle }) {
  const isComplete = status === 'complete';

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 dark:text-gray-300">Status:</span>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isComplete ? 'bg-green-500' : 'bg-yellow-400'
        }`}
        role="switch"
        aria-checked={isComplete}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 shadow ring-0 transition duration-200 ease-in-out ${
            isComplete ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <span className={`text-sm font-medium ${isComplete ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
        {isComplete ? 'Complete' : 'Draft'}
      </span>
    </div>
  );
}

export function EstimateDetail() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);

  // State for coordinating between chart and table
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedLineItems, setExpandedLineItems] = useState(new Set());

  // Vocabulary view toggle (Custom / CSI 50 / CSI 15). Initial mode comes
  // from the tenant's defaultVocabularyMode preference (set during onboarding).
  const tenantPrefs = useTenantPreferences();
  // Sub-trade tenants (anchor division set at onboarding) have every line
  // item bucketed into a single division by design, so the multi-format
  // toggle is meaningless for them and the category header in the
  // breakdown is just visual noise above a flat item list.
  const isSubcontractor = !!tenantPrefs.subcontractorAnchorDivision;
  // Custom-mode tenants don't use CSI MasterFormat, so the "items must be
  // mapped" gate that blocks marking an estimate complete doesn't apply.
  const isCustomMode = tenantPrefs.defaultVocabularyMode === 'custom';
  const {
    viewMode: masterFormatViewMode,
    setViewMode: setMasterFormatViewMode,
    displayCategories,
    isLegacyView,
  } = useMasterFormatView(estimate?.categories, tenantPrefs.defaultVocabularyMode);

  useEffect(() => {
    async function loadEstimate() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await estimatesApi.getEstimateById(estimateId);
        // Default status to 'complete' for historical estimates
        if (!data.status) {
          data.status = data.projectCompleted ? 'complete' : 'draft';
        }
        setEstimate(data);
      } catch (err) {
        setError(err.message || 'Failed to load estimate');
      } finally {
        setIsLoading(false);
      }
    }
    loadEstimate();
  }, [estimateId]);

  const handleStatusToggle = async () => {
    if (!estimate) return;

    const currentStatus = estimate.status || 'complete';

    if (currentStatus === 'complete') {
      // Complete -> Draft requires confirmation
      setShowReopenDialog(true);
    } else {
      // Draft -> Complete: check for unmapped items first, unless the
      // tenant uses a custom vocabulary (subcontractor flow) where CSI
      // mapping isn't expected.
      if (!isCustomMode) {
        const allItems = (estimate.categories || []).flatMap(cat => cat.lineItems || []);
        const unmappedItems = allItems.filter(item => item.isUnmapped || !item.masterformatCode);

        if (unmappedItems.length > 0) {
          showToast(
            `This estimate has ${unmappedItems.length} item${unmappedItems.length !== 1 ? 's' : ''} that still need to be mapped to a MasterFormat code. ` +
            `Please open "Edit Estimate" and map all items before marking the estimate as complete.`,
            'warning',
            8000
          );
          return;
        }
      }

      try {
        await estimatesApi.updateEstimateStatus(estimateId, 'complete');
        setEstimate({ ...estimate, status: 'complete' });
      } catch (err) {
        console.error('Failed to update status:', err);
        setError('Failed to update status. Please try again.');
      }
    }
  };

  const handleReopenConfirm = async () => {
    try {
      await estimatesApi.updateEstimateStatus(estimateId, 'draft');
      setEstimate({ ...estimate, status: 'draft' });
      setShowReopenDialog(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update status. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await estimatesApi.deleteEstimate(estimateId);
      setShowDeleteDialog(false);
      navigate('/estimates');
    } catch (err) {
      console.error('Failed to delete estimate:', err);
      setError('Failed to delete estimate. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="p-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <h2 className="font-semibold mb-2">Error Loading Estimate</h2>
          <p>{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/estimates')}>
            Back to Estimates
          </Button>
        </div>
      </div>
    );
  }

  if (!estimate) return null;

  const isDraft = estimate.status === 'draft';

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/estimates')}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </Button>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">{estimate.projectName}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1 break-words">{estimate.clientName}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:gap-3">
          {/* MasterFormat View Toggle -- hidden for sub-trade tenants
              who only ever produce a single-division view anyway. */}
          {!isSubcontractor && (
            <MasterFormatViewToggle
              viewMode={masterFormatViewMode}
              onToggle={setMasterFormatViewMode}
            />
          )}

          {/* Deep-link to the original Jobber record (when this estimate
              was imported from Jobber). Opens in a new tab so it doesn't
              clobber the user's place in quotethisjob. */}
          {estimate.jobberWebUri && (
            <a
              href={estimate.jobberWebUri}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-md transition-colors"
              title="Open the original record in Jobber"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">View in Jobber</span>
            </a>
          )}

          {/* Status Toggle */}
          <StatusToggle
            status={estimate.status || 'complete'}
            onToggle={handleStatusToggle}
          />

          {/* Action Buttons */}
          {isDraft ? (
            <Button variant="outline" onClick={() => navigate(`/estimates/${estimateId}/edit`, { state: { viewMode: masterFormatViewMode } })}>
              <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="hidden sm:inline">Edit Estimate</span>
            </Button>
          ) : (
            <Badge variant="green" size="lg">
              Complete (Read-Only)
            </Badge>
          )}

          {/* Delete Button */}
          <button
            onClick={handleDeleteClick}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            title="Delete estimate"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Cost Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Cost Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <CostSummary estimate={estimate} />
        </CardContent>
      </Card>

      {/* Project Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ProjectInfo
            estimate={estimate}
            estimateId={estimateId}
            onUpdate={(updates) => setEstimate(prev => ({ ...prev, ...updates }))}
            isDraft={isDraft}
            isSubcontractor={isSubcontractor}
          />
        </CardContent>
      </Card>

      {/* Supporting Documents */}
      <EstimateDocumentsPanel estimateId={estimateId} editable={isDraft} />

      {/* Cost Breakdown Chart */}
      {displayCategories && displayCategories.length > 0 && (
        <CostBreakdownChart
          categories={displayCategories}
          totalCost={estimate.totalCost}
          baseTotalCost={estimate.baseTotalCost || 0}
          isLegacyView={isLegacyView}
          onCategoryClick={(categoryId) => {
            setExpandedCategories((prev) => {
              const next = new Set(prev);
              if (!next.has(categoryId)) {
                next.add(categoryId);
              }
              return next;
            });
          }}
          onLineItemClick={(categoryId, lineItemId) => {
            // Expand the category if not already expanded
            setExpandedCategories((prev) => {
              const next = new Set(prev);
              if (!next.has(categoryId)) {
                next.add(categoryId);
              }
              return next;
            });
            // Expand the line item
            setExpandedLineItems((prev) => {
              const next = new Set(prev);
              if (!next.has(lineItemId)) {
                next.add(lineItemId);
              }
              return next;
            });
            // Scroll to the line item after a short delay to let the DOM update
            setTimeout(() => {
              const element = document.getElementById(`line-item-${lineItemId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Add a brief highlight effect
                element.classList.add('bg-yellow-100', 'dark:bg-yellow-900/40');
                setTimeout(() => {
                  element.classList.remove('bg-yellow-100', 'dark:bg-yellow-900/40');
                }, 1500);
              }
            }, 100);
          }}
        />
      )}

      {/* Cost Breakdown */}
      {displayCategories && displayCategories.length > 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Detailed Cost Breakdown</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isSubcontractor ? (
                  <>
                    {displayCategories.reduce((sum, c) => sum + (c.lineItems?.length || 0), 0)} line items
                  </>
                ) : isLegacyView ? (
                  <>
                    {displayCategories.length} divisions (1995 format), {' '}
                    {displayCategories.reduce((sum, c) => sum + (c.lineItems?.length || 0), 0)} line items
                  </>
                ) : (
                  <>
                    {displayCategories.length} categories, {' '}
                    {displayCategories.reduce((sum, c) => sum + (c.lineItems?.length || 0), 0)} line items
                  </>
                )}
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <CostBreakdownTable
              categories={displayCategories}
              totalCost={estimate.totalCost}
              baseTotalCost={estimate.baseTotalCost || 0}
              estimateId={estimate.estimateId}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              expandedLineItems={expandedLineItems}
              setExpandedLineItems={setExpandedLineItems}
              isLegacyView={isLegacyView}
              flatten={isSubcontractor}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <svg
              className="w-12 h-12 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">No detailed cost breakdown available for this estimate.</p>
          </CardContent>
        </Card>
      )}

      {/* Source File Info */}
      {estimate.sourceFile && (
        <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          <p>Source: {estimate.sourceFile}</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Estimate"
        message="Are you sure you want to delete this estimate? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Reopen Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReopenDialog}
        title="Reopen Estimate"
        message="Are you sure you want to reopen this completed estimate? It will become editable again."
        confirmLabel="Reopen"
        confirmVariant="primary"
        onConfirm={handleReopenConfirm}
        onCancel={() => setShowReopenDialog(false)}
      />
    </div>
  );
}

export default EstimateDetail;
